import { useRef, type ReactNode, type MouseEvent } from "react";

// Wraps the standard `modal-backdrop` div with safe close-on-backdrop-
// click behaviour. The naive `onClick={onClose}` pattern collapses the
// dialog whenever the user mousedowns inside (e.g. selecting text in a
// settings field or a share URL) and releases outside the card — the
// browser's click target is the backdrop, so the modal closes mid-
// selection. Here we require both the press and the release to land
// directly on the backdrop element itself before invoking onClose.
//
// Pass `onClose={null}` to disable backdrop-click close entirely
// (matches the previous "no onClick" pattern used by some modals).
interface Props {
  onClose: (() => void) | null;
  children: ReactNode;
  className?: string;
}

export default function ModalBackdrop({
  onClose,
  children,
  className = "modal-backdrop",
}: Props) {
  // Tracks whether the mousedown that started the current click landed
  // on the backdrop itself. If it started on a child (a drag-select
  // inside the card), we must not close on the resulting click.
  const pressOnBackdropRef = useRef(false);

  function handleMouseDown(e: MouseEvent<HTMLDivElement>) {
    pressOnBackdropRef.current = e.target === e.currentTarget;
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    if (!onClose) return;
    if (e.target !== e.currentTarget) return;
    if (!pressOnBackdropRef.current) return;
    pressOnBackdropRef.current = false;
    onClose();
  }

  return (
    <div
      className={className}
      role="presentation"
      onMouseDown={onClose ? handleMouseDown : undefined}
      onClick={onClose ? handleClick : undefined}
    >
      {children}
    </div>
  );
}
